import { useEffect, useRef, useState } from "react";

// Видео-плеер: если известны размеры (width/height из админки) — рендерим сразу
// с правильным aspect-ratio, без preload="metadata" probe. Это убирает 1–2 сек
// placeholder при открытии экрана.
// Если размеров нет (старые записи без backfill) — fallback: невидимый probe-элемент
// читает метаданные, ставим aspect-ratio как только узнаём.
// Сам <video> без controls — рисуем кастомный UI как у AudioPlayer:
// сверху скраббер + таймкоды, снизу −5 / Play / +5.

type Props = {
  url: string;
  title?: string | null;
  // Опциональные размеры — если знаем заранее (заполнено при upload через админку).
  width?: number | null;
  height?: number | null;
  // Длинная подпись под плеером — мелким приглушённым шрифтом, ниже кнопок.
  caption?: string | null;
  // GIF-режим: автоплей + цикл + без звука + без кастомного UI (нет скраба
  // и кнопок). Включается для конвертированных из GIF mp4. По умолчанию false.
  loop?: boolean;
};

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function VideoPlayer({ url, title, width, height, caption, loop }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);
  // Если знаем размеры заранее — сразу выставляем aspect-ratio без probe.
  const knownRatio =
    width != null && height != null && width > 0 && height > 0
      ? width / height
      : null;
  const [aspectRatio, setAspectRatio] = useState<number | null>(knownRatio);

  // Probe только если размеров не передали — для старых записей без backfill.
  useEffect(() => {
    if (knownRatio !== null) {
      setAspectRatio(knownRatio);
      return;
    }
    setAspectRatio(null);
    const probe = document.createElement("video");
    probe.preload = "metadata";
    probe.muted = true;
    probe.playsInline = true;
    let cancelled = false;
    const onMeta = () => {
      if (cancelled) return;
      const vw = probe.videoWidth;
      const vh = probe.videoHeight;
      if (vw > 0 && vh > 0) {
        setAspectRatio(vw / vh);
      }
    };
    probe.addEventListener("loadedmetadata", onMeta);
    probe.src = url;
    return () => {
      cancelled = true;
      probe.removeEventListener("loadedmetadata", onMeta);
      probe.removeAttribute("src");
      probe.load();
    };
  }, [url, knownRatio]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      if (!seeking) setCurrentTime(v.currentTime);
    };
    const onMeta = () => {
      setDuration(v.duration || 0);
    };
    const onEnded = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("durationchange", onMeta);
    v.addEventListener("ended", onEnded);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("durationchange", onMeta);
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [seeking, aspectRatio]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const jump = (delta: number) => {
    const v = videoRef.current;
    if (!v) return;
    const next = Math.max(0, Math.min(duration || v.duration || 0, v.currentTime + delta));
    v.currentTime = next;
    setCurrentTime(next);
  };

  const onScrubChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const x = parseFloat(e.target.value);
    setCurrentTime(x);
  };

  const onScrubCommit = (e: React.ChangeEvent<HTMLInputElement> | React.PointerEvent<HTMLInputElement>) => {
    const v = videoRef.current;
    if (!v) return;
    const target = e.target as HTMLInputElement;
    const x = parseFloat(target.value);
    v.currentTime = x;
    setSeeking(false);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // GIF-режим: ни таймеров, ни состояния play. Просто <video> с loop/autoplay/muted.
  // Aspect-ratio либо известен из props, либо probe из useEffect выше выставляет
  // через state. Если ни того, ни другого — fallback на 1:1 чтобы не было прыжка.
  if (loop) {
    const ratio = aspectRatio ?? 1;
    return (
      <div className="video-player video-player--gif">
        {title && <div className="video-player-title">{title}</div>}
        <div
          className="video-player-stage"
          style={{ aspectRatio: `${ratio}` }}
        >
          <video
            className="video-player-video"
            src={url}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
          />
        </div>
        {caption && <p className="media-caption">{caption}</p>}
      </div>
    );
  }

  return (
    <div className="video-player">
      {title && <div className="video-player-title">{title}</div>}

      {aspectRatio === null ? (
        <div className="video-player-stage-placeholder" aria-busy="true" />
      ) : (
        <div
          className="video-player-stage"
          style={{ aspectRatio: `${aspectRatio}` }}
          onClick={togglePlay}
        >
          <video
            ref={videoRef}
            className="video-player-video"
            src={url}
            preload="auto"
            playsInline
            controls={false}
          />
        </div>
      )}

      <div className="video-player-scrub">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={onScrubChange}
          onPointerDown={() => setSeeking(true)}
          onPointerUp={onScrubCommit}
          onPointerCancel={onScrubCommit}
          aria-label="Прогресс"
          style={{
            background: `linear-gradient(to right,
              var(--tg-theme-button-color, #2481cc) 0%,
              var(--tg-theme-button-color, #2481cc) ${progress}%,
              var(--card-surface-strong) ${progress}%,
              var(--card-surface-strong) 100%)`,
          }}
        />
      </div>

      <div className="video-player-times">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      <div className="video-player-row">
        <button
          type="button"
          className="video-player-jump"
          onClick={() => jump(-5)}
          aria-label="Назад 5 секунд"
        >
          <span className="video-player-jump-num">−5</span>
        </button>

        <button
          type="button"
          className="video-player-play"
          onClick={togglePlay}
          aria-label={playing ? "Пауза" : "Воспроизвести"}
        >
          {playing ? (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M7 5.5v13a1 1 0 0 0 1.55.832l10-6.5a1 1 0 0 0 0-1.664l-10-6.5A1 1 0 0 0 7 5.5z" />
            </svg>
          )}
        </button>

        <button
          type="button"
          className="video-player-jump"
          onClick={() => jump(5)}
          aria-label="Вперёд 5 секунд"
        >
          <span className="video-player-jump-num">+5</span>
        </button>
      </div>

      {caption && <p className="media-caption">{caption}</p>}
    </div>
  );
}
